import {
    Component,
    OnInit,
    ViewChild,
    ElementRef,
    OnDestroy,
    ViewChildren,
    QueryList,
} from '@angular/core';
import { showOpacityAnimation } from '../../../animations/show-left-right.animation';
import {
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { dateValidator } from 'src/app/directives/date.validator';
import { AuthService } from 'src/app/services/auth.service';
import { LoadService } from 'src/app/services/application/load.service';
import { Router } from '@angular/router';
import {
    IAcceptDocs,
    ICreatePatient,
    onlyCharsWithHyphenRegex,
} from 'src/app/interfaces/patient.interfaces';
import { snilsValidator } from 'src/app/directives/snils.validator';
import { ICaptcha, ICaptchaData } from 'src/app/services/captcha/interfaces';
import { CaptchaService } from 'src/app/services/captcha/captcha.service';
import { Utils } from 'src/app/services/utils';
import { catchError, forkJoin, lastValueFrom, map, Observable, of } from 'rxjs';
import { DateTime } from 'luxon';
import { HttpClient } from '@angular/common/http';
import { TAcceptionTypes } from 'src/app/interfaces/patient.types';
@Component({
    selector: 'app-auth-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['../_auth-shared-styles.scss'],
    animations: [showOpacityAnimation],
    standalone: false,
})
export class RegistrationComponent implements OnInit, OnDestroy {
    public page: 'mainData' | 'snilsPhoneMail' | 'agreement' = 'mainData'; //'mainData'
    public AuthFormTitle: string = '';
    public RegParamList: string = '';
    public currentStep: number = 30;
    public errorMessage: string = '';
    public patientData: ICreatePatient = {
        firstName: '',
        lastName: '',
        secondName: '',
        birthDate: '',
        sex: 0,
    };
    public loading: boolean = false;
    public captchaLoad: boolean = false;
    public captcha: ICaptcha = { id: '', svg: '' };
    public agreementRequired: boolean = false;
    // Если надо добавить новый документ, добавить сюда по аналогии,
    // где name - необходимое название файла в ассетах, по нему будет поиск
    // и в TAcceptionTypes добавить то как тип должен быть назван
    public agreementDocs: Array<IAcceptDocs> = [
        {
            name: 'acception_pd',
            type: 'PERSDATA',
            exist: false,
            label: 'политикой в отношении персональных данных',
        },
        {
            name: 'acception_ls',
            type: 'LKUSE',
            exist: false,
            label: 'условиями использования сервиса личного кабинета',
        },
    ];
    private readonly DYNAMIC_FIELDS = ['snils', 'inn', 'phone', 'secondName'];
    public activeDocFields: string[] = [];
    public maxBdate = DateTime.now().toFormat('yyyy-MM-dd');
    public formRegistry: FormGroup = new FormGroup({
        firstName: new FormControl<string>('', [
            Validators.minLength(1),
            Validators.required,
            Validators.pattern(onlyCharsWithHyphenRegex),
        ]),
        lastName: new FormControl<string>('', [
            Validators.minLength(1),
            Validators.required,
            Validators.pattern(onlyCharsWithHyphenRegex),
        ]),
        birthDate: new FormControl('', [Validators.required, dateValidator()]),
        sex: new FormControl('', [Validators.required]),
    });
    public formSnilsPhoneMail: FormGroup = new FormGroup({
        email: new FormControl<string>('', [
            Validators.required,
            Validators.email,
        ]),
    });
    public formAgree: FormGroup = new FormGroup({
        captcha: new FormControl<string>('', [
            Validators.minLength(6),
            Validators.maxLength(6),
            Validators.required,
        ]),
    });
    @ViewChild('InputBlockLastName') public InputBlockLastName:
        ElementRef | undefined;
    @ViewChildren('inputField') public inputs!: QueryList<ElementRef>;
    public constructor(
        private load: LoadService,
        private auth: AuthService,
        private router: Router,
        private captchaService: CaptchaService,
        private http: HttpClient,
    ) {
        this.AuthFormTitle =
            this.load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.LOGIN
                .AUTH_FORM_TEXT ?? 'Личный кабинет пациента';
        this.RegParamList =
            this.load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.REGISTRATION
                .REGISTRATION_PARAMS_LIST ??
            'sex*;lastName*;firstName*;birthDate*;email*';
        this.agreementRequired =
            !!this.load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.REGISTRATION
                .USER_AGREEMENTS_REQUIRED;
        this.initDynamicFields();
    }
    public ngOnInit(): void {
        this.updateAcceptionDocsExist();
    }
    public ngOnDestroy(): void {}

    public initDynamicFields() {
        const confifParts = this.RegParamList.split(';').map((part) =>
            part.trim(),
        );
        confifParts.forEach((part) => {
            const isRequired = part.endsWith('*');
            const fieldName = part.replace('*', '').trim();
            if (this.DYNAMIC_FIELDS.includes(fieldName)) {
                this.addDynamicControl(fieldName, isRequired);
                this.activeDocFields.push(fieldName);
            }
        });
    }
    private addDynamicControl(fieldName: string, isRequired: boolean): void {
        const validators: ValidatorFn[] = [];
        if (isRequired) validators.push(Validators.required);
        switch (fieldName) {
            case 'snils':
                validators.push(snilsValidator);
                this.formSnilsPhoneMail.addControl(
                    fieldName,
                    new FormControl<string>('', validators),
                );
                break;
            case 'inn':
                validators.push(
                    Validators.minLength(12),
                    Validators.maxLength(12),
                );
                this.formSnilsPhoneMail.addControl(
                    fieldName,
                    new FormControl<string>('', validators),
                );
                break;
            case 'phone':
                validators.push(Validators.minLength(11));
                this.formSnilsPhoneMail.addControl(
                    fieldName,
                    new FormControl<string>('', validators),
                );
                break;
            case 'secondName':
                validators.push(
                    Validators.minLength(1),
                    Validators.pattern(onlyCharsWithHyphenRegex),
                );
                this.formRegistry.addControl(
                    fieldName,
                    new FormControl<string>('', validators),
                );
                break;
            default:
                return;
        }
    }

    public onSubmit1stepReg() {
        this.loading = true;
        this.patientData = {
            firstName: this.formRegistry.controls['firstName'].value,
            lastName: this.formRegistry.controls['lastName'].value,
            birthDate: this.formRegistry.controls['birthDate'].value,
            sex: Number(this.formRegistry.controls['sex'].value),
            ...(this.activeDocFields.includes('secondName') && {
                secondName:
                    this.formRegistry.controls['secondName'].value || null,
            }),
        };
        this.currentStep = 70;
        this.loading = false;
        this.page = 'snilsPhoneMail';
    }
    public onSubmit2stepReg() {
        this.loading = true;
        this.patientData = {
            ...this.patientData,
            email: this.formSnilsPhoneMail.controls['email'].value,
            ...(this.activeDocFields.includes('snils') && {
                snils: this.formSnilsPhoneMail.controls['snils'].value || null,
            }),
            ...(this.activeDocFields.includes('phone') && {
                phone: this.formSnilsPhoneMail.controls['phone'].value || null,
            }),
            ...(this.activeDocFields.includes('inn') && {
                inn: this.formSnilsPhoneMail.controls['inn'].value || null,
            }),
        };
        this.currentStep = 100;
        this.getCaptcha();
        this.loading = false;
        this.page = 'agreement';
    }
    public async onSubmit3stepReg() {
        this.loading = true;
        const captchaData: ICaptchaData = {
            id: this.captcha?.id,
            value: this.formAgree.controls['captcha'].value,
        };
        // Сначала блок проверки капчи! Если он не успешен, не отправляем заявку на регистрацию, данные сохраняются, капча рефрешится, ошибка выведется
        try {
            const captchaVerifyResponse = await lastValueFrom(
                this.captchaService.verifyCaptcha(captchaData),
            );
            if (captchaVerifyResponse.captchaHash) {
                this.patientData = {
                    ...this.patientData,
                    captchaHash: captchaVerifyResponse.captchaHash,
                };
            }
        } catch (err: unknown) {
            this.errorMessage = Utils.getErrorMessage(
                err,
                'CAPTCHA устарела или код введён неверно',
            );
            this.refreshCaptcha();
            this.formAgree.get('captcha')?.reset();
            this.loading = false;
        }
        // После уже, если получили хэш, отправляем запрос на регистрацию
        if (this.patientData.captchaHash) {
            try {
                const responseReg = await lastValueFrom(
                    this.auth.reg$(this.patientData),
                );
                if (responseReg) {
                    this.router.navigate(['auth/register/success']);
                }
            } catch (err: unknown) {
                this.errorMessage = Utils.getErrorMessage(
                    err,
                    'Неизвестная ошибка при регистрации',
                );
                this.refreshCaptcha();
                this.formAgree.get('captcha')?.reset();
                this.patientData.captchaHash = '';
                this.loading = false;
            }
        }
    }

    public goToLogin() {
        this.router.navigate(['auth/login']);
    }
    public goBack() {
        switch (this.page) {
            case 'agreement':
                this.page = 'snilsPhoneMail';
                this.currentStep = 70;
                break;
            case 'snilsPhoneMail':
                this.page = 'mainData';
                this.currentStep = 30;
                break;
            case 'mainData':
                this.router.navigate(['auth/login']);
                break;
        }
    }
    public removeSpaces(event: Event): void {
        const input = event.target as HTMLInputElement;
        input.value = input.value.replace(/\s/g, '');
    }
    public correctNameInput(event: Event, controlName: string): void {
        const input = event.target as HTMLInputElement;

        let value = input.value;

        value = value.replace(/[^a-zA-Zа-яА-ЯёЁ-]/g, '');

        value = value.replace(/^-+/, '');

        value = value.replace(/-{2,}/g, '-');

        if (input.value !== value) {
            input.value = value;
        }

        this.formRegistry.get(controlName)?.setValue(value, {
            emitEvent: false,
        });
    }

    public dateInputType: 'text' | 'date' = 'text';

    @ViewChild('inputField')
    public inputField!: ElementRef<HTMLInputElement>;

    public ngAfterViewInit(): void {
        this.dateInputType = this.birthDateControl.value ? 'date' : 'text';
    }
    public onDateFocus(): void {
        this.dateInputType = 'date';
    }
    public onDateBlur(): void {
        if (!this.birthDateControl.value) {
            this.dateInputType = 'text';
        }
    }
    public get birthDateControl() {
        return this.formRegistry.controls['birthDate'];
    }
    public moveToNextField(event: Event): void {
        const key = event as KeyboardEvent;
        const currentIndex = this.inputs
            .toArray()
            .findIndex((input) => input.nativeElement === key.target);

        if (currentIndex >= 0 && currentIndex < this.inputs.length - 1) {
            this.inputs.toArray()[currentIndex + 1].nativeElement.focus();
        }
    }
    public async getCaptcha() {
        this.captchaLoad = true;
        this.captcha = await this.captchaService.fetchCaptcha();
        this.captchaLoad = false;
    }
    public refreshCaptcha() {
        this.captcha = { id: '', svg: '' };
        this.formAgree.controls['captcha']?.setValue('');
        this.getCaptcha();
    }
    public isRequired(form: FormGroup, fieldName: string) {
        return form.get(fieldName)?.hasValidator(Validators.required) ?? false;
    }

    public focusNextInput(event: Event): void {
        event.preventDefault();

        const target = event.target as HTMLElement;
        const form = target.closest('form');

        if (!form) {
            return;
        }

        const inputs = Array.from(
            form.querySelectorAll<HTMLInputElement>(
                'input:not([disabled]):not([readonly])',
            ),
        );

        const currentIndex = inputs.indexOf(target as HTMLInputElement);
        const nextInput = inputs[currentIndex + 1];

        if (nextInput) {
            nextInput.focus();
            return;
        }

        const button = form.querySelector<HTMLButtonElement>(
            'button[type="submit"]:not([disabled])',
        );

        if (button) {
            button.click();
        }

        target.blur();
    }
    public updateAcceptionDocsExist() {
        // Создаем массив запросов
        const requests = this.agreementDocs.map((doc) => {
            const fullPath = `./assets/${doc.name}.pdf`;
            return this.checkAcceptionDocsExist(fullPath).pipe(
                map((exist) => ({ doc, exist, fullPath })),
            );
        });

        // После выполнения всех проверяем, существуют ли они
        forkJoin(requests).subscribe((results) => {
            results.forEach(({ doc, exist, fullPath }) => {
                if (exist) {
                    doc.exist = true;
                    doc.url = fullPath;
                    // добавляем соответствующий контрол в форму
                    this.formAgree.addControl(
                        doc.type,
                        new FormControl<boolean>(false),
                    );
                    // и, если параметр тру, накидываем валидатор
                    if (this.agreementRequired) {
                        let control = this.formAgree.get(doc.type);

                        if (control) {
                            control.setValidators([Validators.requiredTrue]);
                            control.updateValueAndValidity();
                        }
                    }
                } else {
                    console.warn(doc.name, 'не найден');
                    const control = this.formAgree.get(doc.type);
                    if (control) {
                        control.clearValidators();
                        control.updateValueAndValidity();
                    }
                }
            });
        });
    }
    public checkAcceptionDocsExist(path: string): Observable<boolean> {
        return this.http.request('HEAD', path).pipe(
            map(() => true),
            catchError(() => of(false)),
        );
    }
}
